import React, { useState, useEffect, useCallback } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useAuth } from '../../contexts/AuthContext';
import { DatabaseService } from '../../services/DatabaseService';
import VoteSecurity from '../../utils/VoteSecurity';

// Draggable option component for ranking
const DraggableOption = ({ id, text, index, moveOption }) => {
  // Set up drag functionality
  const [{ isDragging }, drag] = useDrag({
    type: 'option',
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Set up drop functionality
  const [, drop] = useDrop({
    accept: 'option',
    hover: (item, monitor) => {
      if (!monitor.isOver({ shallow: true })) {
        return;
      }
      if (item.index === index) {
        return;
      }
      moveOption(item.index, index);
      item.index = index;
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`p-4 mb-2 bg-white rounded-lg border ${
        isDragging ? 'opacity-50' : ''
      } cursor-grab`}
    >
      <div className="flex items-center">
        <span className="w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-800 rounded-full font-medium">
          {index + 1}
        </span>
        <span className="ml-3 text-gray-900">{text}</span>
      </div>
    </div>
  );
};

// Helper to detect touch devices for backend selection
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Select appropriate DnD backend based on device type
const DndBackend = isTouchDevice() ? TouchBackend : HTML5Backend;

const VotingInterface = ({ poll, pollId, participantId }) => {
  // State management
  const [options, setOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [alreadyVoted, setAlreadyVoted] = useState(false);
  
  const { currentUser } = useAuth();
  
  // Check if user has already voted
  useEffect(() => {
    if (pollId) {
      // Check local storage and cookies first
      const hasVoted = VoteSecurity.hasVoted(pollId);
      setAlreadyVoted(hasVoted);
      
      // If they have a participant ID, also check the database
      if (participantId && !hasVoted) {
        const checkParticipantStatus = async () => {
          try {
            const participantDoc = await DatabaseService.getParticipant(pollId, participantId);
            if (participantDoc && participantDoc.hasVoted) {
              setAlreadyVoted(true);
            }
          } catch (err) {
            console.error('Error checking participant status:', err);
          }
        };
        
        checkParticipantStatus();
      }
    }
  }, [pollId, participantId]);
  
  // Initialize options from poll data
  useEffect(() => {
    if (poll && poll.options) {
      // Sort options by initial order
      const sortedOptions = [...poll.options].sort((a, b) => a.order - b.order);
      setOptions(sortedOptions);
    }
  }, [poll]);
  
  // Handle reordering of options (drag and drop)
  const moveOption = useCallback((dragIndex, hoverIndex) => {
    setOptions((prevOptions) => {
      const newOptions = [...prevOptions];
      const draggedOption = newOptions[dragIndex];
      newOptions.splice(dragIndex, 1);
      newOptions.splice(hoverIndex, 0, draggedOption);
      return newOptions;
    });
  }, []);
  
  // Reset options to original order
  const resetRanking = () => {
    if (poll && poll.options) {
      const sortedOptions = [...poll.options].sort((a, b) => a.order - b.order);
      setOptions(sortedOptions);
    }
  };
  
  // Submit vote to the database
  const handleSubmitVote = async () => {
    // Double-check that the user hasn't already voted
    if (VoteSecurity.hasVoted(pollId)) {
      setError('You have already voted in this poll');
      setAlreadyVoted(true);
      return;
    }
    
    try {
      setError('');
      setSubmitting(true);
      
      // Format rankings data
      const rankings = {};
      options.forEach((option, index) => {
        rankings[`rank${index + 1}`] = option.id;
      });
      
      // Submit vote to database
      const userId = currentUser ? currentUser.uid : null;
      await DatabaseService.submitVote(pollId, userId, rankings, participantId);
      
      // Record the vote in local storage and cookies
      VoteSecurity.recordVote(pollId, participantId);
      
      setSuccess(true);
    } catch (err) {
      setError('Failed to submit vote: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Already voted message
  if (alreadyVoted) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
          <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="mt-3 text-lg leading-6 font-medium text-gray-900">
          Vote Already Submitted
        </h3>
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            You have already voted in this poll. Each participant can only vote once.
          </p>
        </div>
        <div className="mt-5">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            View Results
          </button>
        </div>
      </div>
    );
  }
  
  // Success message after voting
  if (success) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="mt-3 text-lg leading-6 font-medium text-gray-900">
          Vote submitted successfully!
        </h3>
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Thank you for participating in this poll. You can view the results now.
          </p>
        </div>
        <div className="mt-5">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            View Results
          </button>
        </div>
      </div>
    );
  }
  
  // Main voting interface
  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Rank Your Choices</h2>
      
      <div className="mb-4 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
        <p className="text-sm text-yellow-700">
          Drag and drop the options below to rank them in your preferred order. Your first choice should be at the top.
        </p>
      </div>
      
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {/* Drag and drop interface */}
      <DndProvider backend={DndBackend}>
        <div className="mb-6">
          {options.map((option, index) => (
            <DraggableOption
              key={option.id}
              id={option.id}
              text={option.text}
              index={index}
              moveOption={moveOption}
            />
          ))}
        </div>
      </DndProvider>
      
      {/* Action buttons */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={resetRanking}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Reset Ranking
        </button>
        
        <button
          type="button"
          onClick={handleSubmitVote}
          disabled={submitting}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {submitting ? 'Submitting...' : 'Submit Vote'}
        </button>
      </div>
    </div>
  );
};

export default VotingInterface;